import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as AhoyRtcConfig from '../config/ahoy-webrtc-config';
import {
  ICameraList,
  IReceiveCamerasSuccess,
  IReceiveStreamsSuccess,
  StreamStatus
} from '../interfaces/ahyortc-video-stream-widget.interface';


@Injectable({
  providedIn: 'root'
})
export class VideoStreamService {
  private readonly peerConnections: Map<string, RTCPeerConnection> =
      new Map<string, RTCPeerConnection>();
  private readonly responseSubjects: Map<string, Subject<any>> =
      new Map<string, Subject<any>>();
  private readonly widgetStreamUuidState: Map<string, BehaviorSubject<string | undefined>> =
      new Map<string, BehaviorSubject<string | undefined>>();
  private readonly widgetStreamStatusState: Map<string, BehaviorSubject<StreamStatus>> =
      new Map<string, BehaviorSubject<StreamStatus>>(); // Track stream availability

  private header: HttpHeaders | undefined;

  private readonly http: HttpClient = inject(HttpClient);
  private readonly widgetRtcpTimer = new Map<string, number>

  setRequestHeader(apiKey: string, apiUrl: string): void {
    this.header = new HttpHeaders({
      'X-Proxy-Target': apiUrl,
      'x-api-key': apiKey,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
    })
  }

  getRequestHeader(): HttpHeaders | undefined {
    return this.header;
  }

  // Gets the observable for a specific widgetId
  getStreamUuid$(widgetId: string): Observable<string | undefined> {
    if (!this.widgetStreamUuidState.has(widgetId)) {
      this.widgetStreamUuidState.set(widgetId, new BehaviorSubject<string | undefined>(undefined));
    }
    return this.widgetStreamUuidState.get(widgetId)!.asObservable();
  }

  // Sets a new stream UUID for a specific widgetId
  setStreamUuid(widgetId: string, streamUuid: string | undefined): void {
    if (!this.widgetStreamUuidState.has(widgetId)) {
      this.widgetStreamUuidState.set(widgetId, new BehaviorSubject<string | undefined>(undefined));
    }
    this.widgetStreamUuidState.get(widgetId)!.next(streamUuid);
  }

  // Clean-up
  removeStreamUUid(widgetId: string) {
    const subject = this.widgetStreamUuidState.get(widgetId);
    if (subject) {
      subject.complete(); // Clean up subscribers
      this.widgetStreamUuidState.delete(widgetId);
    }
  }

  // Gets the observable for a specific widgetId's stream status.
  getStreamStatus$(widgetId: string): Observable<StreamStatus> {
    if (!this.widgetStreamStatusState.has(widgetId)) {
      this.widgetStreamStatusState.set(widgetId, new BehaviorSubject<StreamStatus>('notAvailable'));
    }
    return this.widgetStreamStatusState.get(widgetId)!.asObservable();
  }

  // Updates the stream status for a specific widgetId.
  setStreamStatus(widgetId: string, status: StreamStatus): void {
    if (!this.widgetStreamStatusState.has(widgetId)) {
      this.widgetStreamStatusState.set(widgetId, new BehaviorSubject<StreamStatus>('notAvailable'));
    }
    this.widgetStreamStatusState.get(widgetId)!.next(status);
  }

  // Clean-up
  removeStreamStatus(widgetId: string) {
    const subject = this.widgetStreamStatusState.get(widgetId);
    if (subject) {
      subject.complete(); // Clean up subscribers
      this.widgetStreamStatusState.delete(widgetId);
    }
  }

  // Play video stream
  onPlay(videoElement?: HTMLVideoElement): void {
    videoElement?.play();
  }

  // Paused video stream
  onPause(videoElement?: HTMLVideoElement): void {
    videoElement?.pause();
  }

  async availableCameras(): Promise<ICameraList[]>  {
    try {
      const response = await firstValueFrom(this.http.get<IReceiveCamerasSuccess>(
          `${AhoyRtcConfig.apiPath}/cameras`,
          { headers: this.header }
      ));

      if (response?.success) {
        return response.cameras;
      }

    } catch (error) {
      console.error('Failed to receive available cameras request', error);
    }
    return [];
  }

  // Returns available streams from server
  async availableStreams(): Promise<any[]> {
    try {
      const response = await firstValueFrom(this.http.get<IReceiveStreamsSuccess>(
          `${AhoyRtcConfig.apiPath}/streams`,
          { headers: this.header }
      ));

      if (response?.success) {
        return response.streams;
      }

    } catch (error) {
      console.error('Failed to receive available stream request', error);
    }
    return [];
  }

  // Request stream by uuid from server
  async requestVideoStream(uuid: string, widgetId: string): Promise<any> {
    const requestKey = `${widgetId}-${uuid}`;
    if (!this.responseSubjects.has(requestKey)) {
      this.responseSubjects.set(requestKey, new Subject());
    }

    let stopRequestRepeat: boolean = false;
    let reason = '';
    const attemptRequest = async (): Promise<any> => {
      try {
        const response = await firstValueFrom(this.http.get<any>(
            `${AhoyRtcConfig.apiPath}/receive/${uuid}`,
            { headers: this.header }
        ));

        if (response?.success && response.sdp) {
          return response;
        }
      } catch (error: any) {
        if (error?.status === 504 || error?.status === 0) {
          console.error(`Stream request failed. ${error.statusText}, Status: ${error.status}`);
          reason = error.statusText;
          stopRequestRepeat = true;
        } else if (error.status === 404) {
          console.error(`Incorrect stream URL or API KEY. - ${error.statusText}, Status: ${error.status}`);
          reason = error.statusText;
          stopRequestRepeat = true;
        } else {
          console.error(`Stream request failed, retrying.... ${error.statusText}, Status: ${error.status}`);
          stopRequestRepeat = false;
          reason = error.statusText;
        }
      }

      // Stop retrying if a 504, 0 or 404 error encountered
      if (stopRequestRepeat) {
        return { success: false, reason: reason }
      }

      return new Promise(resolve => setTimeout(() =>
          resolve(attemptRequest()), AhoyRtcConfig.timeout)
      );
    };

    return attemptRequest();
  }

  // Monitor observation of the video stream
  async handleStream(uuid: string, widgetId: string, videoElement: HTMLVideoElement): Promise<void> {
    const connectionKey = `${widgetId}-${uuid}`;
    let peerConnection = this.peerConnections.get(connectionKey);

    if (!peerConnection) {
      peerConnection = new RTCPeerConnection();
      this.peerConnections.set(connectionKey, peerConnection);
    }

    let localDescription: RTCSessionDescriptionInit | null = null;
    let endpointUuid: string | null = null;
    let rtcpTimer: any;
    let lastRtpTimestamp = -1;

    (peerConnection as any).onaddstream = async (event: any) =>  {
      if (event.stream) {
        videoElement.srcObject = event.stream;
      }
    };

    const response = await this.requestVideoStream(uuid, widgetId);
    if (!response.success) {
      this.setStreamStatus(widgetId, 'notAvailable');
      return;
    }

    endpointUuid = response.uuid;
    await peerConnection.setRemoteDescription(
        new RTCSessionDescription({ type: "offer", sdp: response.sdp })
    );
    const answer = await peerConnection.createAnswer();

    localDescription = answer;
    await peerConnection.setLocalDescription(localDescription);

    // POST Request mit der Antwort
    await firstValueFrom(this.http.post(`${AhoyRtcConfig.apiPath}/receive/${uuid}/${endpointUuid}`,
        { sdp: localDescription.sdp },
        {
          headers: this.header,
          responseType: 'text'
        }
    ));

    this.setStreamStatus(widgetId, 'available');

    // RTP Stream Überwachung
    rtcpTimer = setInterval(() => {
      let rtpTimestamp = -1;
      peerConnection.getReceivers().forEach((receiver: RTCRtpReceiver) => {
        receiver.getSynchronizationSources().forEach((ssrc: RTCRtpSynchronizationSource) => {
          console.log(`widgetId: ${widgetId} - uuid: ${uuid} |` ,
              new Date(ssrc.timestamp) + ' -> ' + ssrc.rtpTimestamp);
          rtpTimestamp = ssrc.rtpTimestamp;
        });
      });

      if (lastRtpTimestamp >= 0) {
        if (rtpTimestamp === -1 || rtpTimestamp === lastRtpTimestamp) {
          console.log(`RTP stream got stuck, restarting... widgetId: ${widgetId} - uuid: ${uuid}`);
          this.setStreamStatus(widgetId, 'stuck');
          clearInterval(rtcpTimer);
          this.cleanupConnection(connectionKey);
          this.handleStream(uuid, widgetId, videoElement);
        }
      }

      if (rtpTimestamp >= 0) {
        lastRtpTimestamp = rtpTimestamp;
      }
    }, AhoyRtcConfig.rtpObserveInterval);

    this.widgetRtcpTimer.set(widgetId, rtcpTimer);
  }

  // Clean up connections
  cleanupConnection(connectionKey: string): void {
    const peerConnection = this.peerConnections.get(connectionKey);
    if (peerConnection) {
      peerConnection.close();
      this.peerConnections.delete(connectionKey);
    }

    if (this.responseSubjects.has(connectionKey)) {
      this.responseSubjects.get(connectionKey)?.complete();
      this.responseSubjects.delete(connectionKey);
    }

    const widgetId = connectionKey.split('-')[0];
    const rtcpTimer = this.widgetRtcpTimer.get(widgetId);
    if (rtcpTimer) {
      clearInterval(rtcpTimer);
    }
  }
}
